import React, { useState } from 'react';
import axios from 'axios';
import SEO from './SEO';
import './WordAnalyzer.css';

// Use relative URL in production (same domain), absolute URL in development
// If REACT_APP_API_URL is set, use it; otherwise use relative URL (empty string)
// which works for both localhost (when served by backend) and production
const API_URL = process.env.REACT_APP_API_URL || '';

function WordAnalyzer() {
  const [text, setText] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get current domain dynamically (works for both www and non-www)
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://sharvalabs.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Word Frequency Analyzer",
    "url": `${baseUrl}/word-analyzer`,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Web",
    "description": "Analyze text and discover the top 5 most frequent meaningful words with advanced stop word filtering",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Word frequency analysis",
      "Stop word filtering",
      "Top 5 word extraction",
      "Text statistics"
    ]
  };

  const handleAnalyze = async () => {
    if (!text.trim()) {
      setError('Please enter some text to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const apiEndpoint = `${API_URL}/api/analyze`;
      console.log('Calling API:', apiEndpoint); // Debug log
      const response = await axios.post(apiEndpoint, { text });
      setResults(response.data);
    } catch (err) {
      console.error('API Error Details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url
      });
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          'Failed to analyze text. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setText('');
    setResults(null);
    setError(null);
  };

  return (
    <>
      <SEO
        title="Word Frequency Analyzer - Sharva Labs"
        description="Free online word frequency analyzer. Discover the top 5 most frequent meaningful words in your text. Advanced text analysis with automatic stop word filtering."
        path="/word-analyzer"
        keywords="word frequency analyzer, text analyzer, word counter, content analysis, stop words, text processing, word frequency tool"
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <div className="analyzer-container">
        <div className="analyzer-header">
        <h1>Word Frequency Analyzer</h1>
        <p>Enter your text below to discover the top 5 most frequent meaningful words</p>
      </div>

      <div className="analyzer-card">
        <div className="input-section">
          <label htmlFor="text-input" className="input-label">
            Text Input
          </label>
          <textarea
            id="text-input"
            className="text-input"
            placeholder="Paste or type your text here..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows="10"
          />
          <div className="button-group">
            <button
              className="btn btn-primary"
              onClick={handleAnalyze}
              disabled={loading || !text.trim()}
            >
              {loading ? 'Analyzing...' : 'Analyze Text'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section">
            <div className="stats-summary">
              <div className="stat-card">
                <div className="stat-value">{results.totalWords}</div>
                <div className="stat-label">Total Words</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{results.totalWordsAfterFilter}</div>
                <div className="stat-label">Meaningful Words</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{results.words.length}</div>
                <div className="stat-label">Top Words</div>
              </div>
            </div>

            <div className="words-list">
              <h3>Top 5 Most Frequent Words</h3>
              <div className="words-grid">
                {results.words.map((item, index) => (
                  <div key={index} className="word-item">
                    <div className="word-rank">#{index + 1}</div>
                    <div className="word-content">
                      <div className="word-text">{item.word}</div>
                      <div className="word-count">{item.count} {item.count === 1 ? 'time' : 'times'}</div>
                    </div>
                    <div className="word-bar">
                      <div
                        className="word-bar-fill"
                        style={{
                          width: `${(item.count / results.words[0].count) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {results.words.length === 0 && (
                <p className="no-results">No meaningful words found after filtering stop words.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default WordAnalyzer;

